import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { notificationAPI } from '../../lib/api';
import { toast } from 'sonner';
import {
  Bell,
  CreditCard,
  Wrench,
  AlertTriangle,
  CheckCircle,
  Info,
  CheckCheck,
} from 'lucide-react';
import { useI18n } from '../../context/I18nContext';

const categoryIcons = {
  payment: CreditCard,
  maintenance: Wrench,
  late_fee: AlertTriangle,
  general: Info,
};

const categoryColors = {
  payment: 'text-emerald-400 bg-emerald-500/10',
  maintenance: 'text-blue-400 bg-blue-500/10',
  late_fee: 'text-red-400 bg-red-500/10',
  general: 'text-gold bg-gold/10',
};

const Notifications = () => {
  const { t } = useI18n();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => { loadNotifications(); }, []);

  const loadNotifications = async () => {
    try {
      const res = await notificationAPI.getAll();
      setNotifications(res.data.notifications);
      setUnreadCount(res.data.unread_count);
    } catch (error) {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await notificationAPI.markRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const filtered = filter === 'all'
    ? notifications
    : filter === 'unread'
      ? notifications.filter(n => !n.read)
      : notifications.filter(n => n.category === filter);

  if (loading) {
    return (
      <DashboardLayout type="tenant" title={t('tenant.notifications')}>
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => <div key={i} className="h-20 bg-card animate-pulse" />)}
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout type="tenant" title={t('tenant.notifications')}>
      <div className="space-y-6" data-testid="notifications-page">
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground">
            {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}` : 'All caught up!'}
          </p>
          {unreadCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              className="border-gold/30 text-gold hover:bg-gold/10"
              onClick={handleMarkAllRead}
              data-testid="mark-all-read-btn"
            >
              <CheckCheck className="w-4 h-4 mr-2" /> Mark All Read
            </Button>
          )}
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 flex-wrap">
          {[
            { key: 'all', label: 'All' },
            { key: 'unread', label: 'Unread' },
            { key: 'payment', label: 'Payments' },
            { key: 'maintenance', label: 'Maintenance' },
            { key: 'late_fee', label: 'Late Fees' },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-4 py-1.5 text-xs uppercase tracking-wider border transition-all duration-200 ${
                filter === f.key
                  ? 'bg-gold/10 text-gold border-gold/30'
                  : 'bg-card text-muted-foreground border-border hover:border-gold/20'
              }`}
              data-testid={`filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Notification list */}
        {filtered.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="py-16 text-center">
              <Bell className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-xl font-serif mb-2">No Notifications</h3>
              <p className="text-muted-foreground">
                {filter === 'unread' ? "You're all caught up!" : 'Nothing here yet'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {filtered.map((n, index) => {
              const Icon = categoryIcons[n.category] || Info;
              const colorCls = categoryColors[n.category] || categoryColors.general;
              const [iconColor, iconBg] = colorCls.split(' ');

              return (
                <Card
                  key={n.id}
                  className={`bg-card border-border transition-all duration-300 card-animate ${
                    !n.read ? 'border-l-2 border-l-gold' : 'opacity-70'
                  }`}
                  style={{ animationDelay: `${index * 0.03}s` }}
                  data-testid={`notification-${n.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className={`w-10 h-10 ${iconBg} flex items-center justify-center shrink-0`}>
                        <Icon className={`w-5 h-5 ${iconColor}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <h4 className={`text-sm font-medium ${!n.read ? '' : 'text-muted-foreground'}`}>
                              {n.title}
                            </h4>
                            <p className="text-sm text-muted-foreground mt-0.5">{n.message}</p>
                          </div>
                          {!n.read && (
                            <button
                              onClick={() => handleMarkRead(n.id)}
                              className="text-gold hover:text-gold-light transition-colors shrink-0"
                              title="Mark as read"
                              data-testid={`mark-read-${n.id}`}
                            >
                              <CheckCircle className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-2">
                          {new Date(n.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Notifications;
